import { ReactElement, useContext, useState, useEffect } from 'react'

import { getCookie } from 'cookies-next'
import {
  CollectionSellingMethodType,
  Visibility,
  FetchElementAuthorizations,
  FetchElementAuthorizationsVariables,
} from 'globalTypes'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useQuery } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { Trans } from '@lingui/macro'
import CollectionShareWrapper from 'Root/App/Card/Collection/ShareWrapper'
import ContentShareWrapper from 'Root/App/Card/Content/ShareWrapper'
import FolderShareWrapper from 'Root/App/Card/Folder/ShareWrapper'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import Paragraph from 'Root/App/Paragraph'
import Title from 'Root/App/Title'
import FetchMoreLoading from 'Root/Pages/Homepage/App/FetchMoreLoading'
import { getElementType } from 'lib/get-element-type'
import { useCurrentUserId } from 'lib/use-current-user-id'
import filter from 'lodash/filter'
import map from 'lodash/map'

import AddUser from './AddUser'
import Loading from './Loading'
import Authorization from './Row/Authorization'
import Invite from './Row/Invite'
import CopyCheckoutLink from './copy-checkout-link'
import QUERY from './fetch-element-authorizations.graphql'
import GenerateLink from './generate-link'
import UpdateSharePanelVisibility from './update-visibility'

library.add(faCheck)

interface Props {
  id: string
}

const ITEMS_PER_PAGE = 25

const ShareWrapperSwitch = ({ children, element }) => {
  const type = getElementType(element.__typename)

  return type === 'collection' ? (
    <CollectionShareWrapper collection={element as any}>
      {children}
    </CollectionShareWrapper>
  ) : type === 'folder' ? (
    <FolderShareWrapper folder={element as any}>{children}</FolderShareWrapper>
  ) : (
    <ContentShareWrapper content={element as any}>
      {children}
    </ContentShareWrapper>
  )
}

const ShareSectionHeading = ({
  title,
  subtitle,
}: {
  title: ReactElement
  subtitle: ReactElement
}) => (
  <span className="flex flex-col gap-1">
    <Title size="XS">{title}</Title>
    <Paragraph>{subtitle}</Paragraph>
  </span>
)

const ShareBox = ({ id, closePanel }) => {
  const userId = useCurrentUserId()

  const [allItems, setAllItems] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)

  const { data, loading, fetchMore } = useQuery<
    FetchElementAuthorizations,
    FetchElementAuthorizationsVariables
  >(QUERY, {
    variables: { id, page: 1, first: ITEMS_PER_PAGE },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (
      loading ||
      currentPage !== 1 ||
      !data ||
      !data.element ||
      !data.element.paginatedAuthorizations
    )
      return

    const { data: pageData, paginatorInfo } =
      data.element.paginatedAuthorizations
    const filteredItems = filter(
      pageData,
      ({ __typename, ...item }) =>
        __typename === 'Invite' || !('user' in item) || item.user.id !== userId
    )
    setAllItems(filteredItems)
    setHasMoreData(paginatorInfo.hasMorePages)
  }, [loading, data, currentPage])

  const loadMore = async () => {
    if (!hasMoreData) return
    const nextPage = currentPage + 1
    const result = await fetchMore({
      variables: { id, page: nextPage, first: ITEMS_PER_PAGE },
    })
    const conn = result.data?.element?.paginatedAuthorizations
    if (conn) {
      setAllItems((prev) => [...prev, ...conn.data])
      setHasMoreData(conn.paginatorInfo.hasMorePages)
      setCurrentPage(nextPage)
    }
  }

  if (loading && allItems.length === 0) {
    return <Loading />
  }

  if (!data || !data.element) {
    return <Loading />
  }

  const element = data.element

  const elementType = getElementType(element.__typename)

  const showCheckoutLink =
    elementType === 'collection' &&
    element.visibility === Visibility.public &&
    'selling' in element &&
    element.selling &&
    typeof element.sellingPricing === 'number' &&
    element.sellingPricing > 0 &&
    element.sellingMethod === CollectionSellingMethodType.online &&
    !element.need_registration

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility === Visibility.mine) {
      closePanel()
    }
  }

  return (
    <>
      {elementType !== 'folder' && (
        <UpdateSharePanelVisibility
          id={id}
          element={element}
          type={elementType}
          onConfirm={handleOnConfirm}
        />
      )}

      <ShareWrapperSwitch element={element}>
        {showCheckoutLink && (
          <span id="share-by-checkout" className="flex flex-col gap-4">
            <ShareSectionHeading
              title={<Trans>Link al checkout</Trans>}
              subtitle={
                <Trans>
                  Copia il link al checkout del tuo corso per integrarlo nel tuo
                  sito.
                </Trans>
              }
            />
            <CopyCheckoutLink slug={element.slug} />
          </span>
        )}

        <span id="share-by-token" className="flex flex-col gap-4">
          <ShareSectionHeading
            title={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Condividi tramite link privato</Trans>
              ) : (
                <Trans>Condividi tramite link</Trans>
              )
            }
            subtitle={
              element.visibility === Visibility.private ||
              element.visibility === Visibility.protected ? (
                <Trans>Genera un link privato valido una sola volta.</Trans>
              ) : (
                <Trans>Copia il link per condividerlo con chi vuoi.</Trans>
              )
            }
          />
          <GenerateLink visibility={element.visibility} />
        </span>
      </ShareWrapperSwitch>

      <span id="share-by-email" className="flex flex-col gap-4">
        <ShareSectionHeading
          title={<Trans>Condividi tramite email</Trans>}
          subtitle={
            <Trans>
              Manderemo alle persone che inviti un link magico e le istruzioni
              per accedere.
            </Trans>
          }
        />
        <AddUser
          id={element.id}
          knownUsers={data.knownUsers}
          creator={element.creator}
          currentUserData={data.user.emails}
        />
      </span>

      {allItems.length > 0 && (
        <Title size="XS">
          <Trans>Condiviso con</Trans>:
          <hr className="mt-4 divide-gray-200" />
        </Title>
      )}

      <InfiniteScroll
        dataLength={allItems.length}
        next={loadMore}
        hasMore={hasMoreData}
        loader={<FetchMoreLoading />}
        scrollableTarget="slide-container"
      >
        <div role="list" className="flex flex-col divide-y divide-gray-200">
          {map(allItems, (item) =>
            item.__typename === 'Authorization' ? (
              <Authorization
                authorization={item}
                elementId={element.id}
                key={item.id}
              />
            ) : (
              <Invite elementId={element.id} invite={item} key={item.id} />
            )
          )}
        </div>
      </InfiniteScroll>

      {!hasMoreData && allItems.length > 0 && (
        <div className="text-center text-gray-500 mt-5 pb-4">
          <Trans>Tutti i risultati sono stati caricati</Trans>
        </div>
      )}
    </>
  )
}

const SlideContainer = ({ id }: Props) => {
  const { setElementId, subscriptionPlanCode, userSubscriptionLoading } =
    useContext(AuthorizationsContext)
  const [workspaceRole, setWorkspaceRole] = useState(null)

  useEffect(() => {
    const workspaceRole = getCookie('workspace_role')
    setWorkspaceRole(workspaceRole)
  }, [])

  if (userSubscriptionLoading) return null

  const shareUserElement =
    subscriptionPlanCode && subscriptionPlanCode !== 'essential'

  return (
    <div id="slide-container" className="fixed w-full h-full overflow-auto">
      <div className="flex flex-col h-full mx-auto px-4 py-3 max-w-3xl sm:px-6 sm:py-6 gap-6">
        <Title size="M">
          <Trans>Condividi</Trans>
        </Title>

        {(shareUserElement || workspaceRole) && (
          <ShareBox id={id} closePanel={() => setElementId(undefined)} />
        )}
      </div>
    </div>
  )
}

export default SlideContainer
